import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import type { seriesPrimaryContent$key } from "scmp-app/queries/__generated__/seriesPrimaryContent.graphql";

import {
  Container,
  Content,
  CoverImage,
  Headline,
  PublishedDate,
  SponsorContainer,
  SponsorName,
  SponsorType,
  StyledCoverEntityLink,
  StyledEntityLink,
  StyledPlusIcon,
  Summary,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: seriesPrimaryContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref: forwardReference,
  reference: reference_,
  topicLinkVariant,
}) => {
  const content = useFragment(
    graphql`
      fragment seriesPrimaryContent on Content {
        entityId
        urlAlias
        ...entityLink
        ...helpersCheckIsPlusArticle
        ...helpersUseContentSponsorContent
        ...hooksContentItemProvidersContent
          @arguments(
            withCoverImage: true
            withCoverImageSize540x360: true
            withHeadline: true
            withSummary: true
            withPublishedDate: true
            withVideoDurationOverlay: true
          )
      }
    `,
    reference_,
  );
  const providers = useContentItemProviders(content, topicLinkVariant);
  const headline = providers.headline({ hideTooltip: true, preferSocialHeadline: true });
  const isShowPlusFlag = checkIsPlusArticle(content);
  const contentSponsor = getContentSponsor(content);
  const isSponsorArticle = notEmpty(contentSponsor?.name);

  return (
    <Container className={className} ref={forwardReference}>
      <Content>
        {isShowPlusFlag && <StyledPlusIcon />}
        <StyledEntityLink
          onClick={() => onClick?.(content.entityId, content.urlAlias)}
          reference={content}
        >
          <Headline>{headline}</Headline>
        </StyledEntityLink>
        <Summary>
          {providers.summary({
            isTextOverflowEllipsis: true,
          })}
        </Summary>
        {isSponsorArticle ? (
          <SponsorContainer>
            <SponsorType>{contentSponsor?.type}</SponsorType>
            <SponsorName>{contentSponsor?.name}</SponsorName>
          </SponsorContainer>
        ) : (
          <PublishedDate>{providers.publishedDate()}</PublishedDate>
        )}
      </Content>

      <StyledCoverEntityLink
        onClick={() => onClick?.(content.entityId, content.urlAlias)}
        reference={content}
      >
        <CoverImage>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size540x360",
              mobileUp: "size540x360",
              tabletUp: "size540x360",
            },
          })}
        </CoverImage>
        {providers.video?.durationOverlay({
          iconSize: "small",
          variant: "homepage",
        })}
      </StyledCoverEntityLink>
    </Container>
  );
};

Component.displayName = "ContentItemSeriesPrimary";

export const ContentItemSeriesPrimary = withHighlightedHeadline(Component);
